import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShieldCheck, MapPin, Facebook, Instagram, Star } from "lucide-react";
import { PHONE, PHONE_TEL, HOURS } from "../data/content";
import StickyCallBar from "./StickyCallBar";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Home IT Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/suburbs", label: "Suburbs we visit" },
  { to: "/reviews", label: "Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-white/10"
      style={{ backgroundColor: "rgba(5,5,5,0.85)", backdropFilter: "blur(20px)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
            <div className="relative">
              <div
                className="w-9 h-9 flex items-center justify-center"
                style={{ background: "var(--te-primary)" }}
              >
                <ShieldCheck size={20} color="#fff" strokeWidth={2.5} />
              </div>
              <span
                className="absolute -top-1 -right-1 w-2 h-2 rounded-full te-pulse"
                style={{ background: "var(--te-primary)" }}
              />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-lg tracking-tight">TECH EMERGENCY</div>
              <div className="te-overline text-[10px] hidden sm:block whitespace-nowrap">Adelaide · IT Support</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7 mr-4">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/services"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `te-link-underline text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/book"
              data-testid="header-book-btn"
              className="te-btn te-btn-ghost text-sm whitespace-nowrap"
            >
              Send an Enquiry
            </Link>
            <a
              href={PHONE_TEL}
              data-testid="header-call-btn"
              className="te-btn te-btn-primary text-sm whitespace-nowrap"
            >
              <Phone size={16} />
              {PHONE}
            </a>
          </div>

          {/* Mobile menu toggle (call lives in the sticky bottom bar on mobile) */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="text-white p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {open && (
          <div className="lg:hidden pb-4 border-t border-white/10 pt-4 space-y-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block px-2 py-3 text-sm ${isActive ? "text-white" : "text-zinc-400"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-btn"
              className="te-btn te-btn-ghost w-full mt-2"
            >
              Send an Enquiry
            </Link>
            <a
              href={PHONE_TEL}
              data-testid="mobile-call-btn-menu"
              className="te-btn te-btn-primary w-full mt-2"
            >
              <Phone size={16} />
              Call {PHONE}
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer
      className="border-t border-white/10 mt-10"
      style={{ backgroundColor: "var(--te-paper)" }}
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-8 h-8 flex items-center justify-center"
              style={{ background: "var(--te-primary)" }}
            >
              <ShieldCheck size={18} color="#fff" />
            </div>
            <div className="font-bold tracking-tight">TECH EMERGENCY</div>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Mobile computer repairs and home IT support across metropolitan Adelaide.
            Owner-operated. Police-checked. Local.
          </p>
        </div>

        <div>
          <div className="te-overline mb-4">Home IT Services</div>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/services/computer-repairs" className="hover:text-white">Computer Repairs</Link></li>
            <li><Link to="/services/tech-support" className="hover:text-white">Tech Support · Apple &amp; Windows</Link></li>
            <li><Link to="/services/wifi-troubleshooting" className="hover:text-white">Home Wi-Fi Troubleshooting</Link></li>
            <li><Link to="/services/unifi-home-networking-adelaide" className="hover:text-white">Home UniFi Networking (Ubiquiti)</Link></li>
            <li><Link to="/services/starlink-setup-troubleshooting-adelaide" className="hover:text-white">Starlink Setup &amp; Troubleshooting</Link></li>
            <li><Link to="/services/email-solutions" className="hover:text-white">Email &amp; Microsoft 365 (Home)</Link></li>
            <li><Link to="/services/data-recovery" className="hover:text-white">Data Backup &amp; Recovery</Link></li>
            <li><Link to="/services/anti-scamming-cyber-awareness" className="hover:text-white">Anti-Scamming &amp; Virus Removal</Link></li>
            <li className="pt-1"><Link to="/services" className="hover:text-white font-medium" style={{ color: "var(--te-primary)" }}>All home services →</Link></li>
          </ul>
        </div>

        <div>
          <div className="te-overline mb-4">Coverage</div>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><MapPin size={14} className="inline mr-1" /> Adelaide Metro</li>
            <li>North · East · South · West · CBD</li>
            <li><Link to="/suburbs" className="hover:text-white">All suburbs we cover →</Link></li>
          </ul>
        </div>

        <div>
          <div className="te-overline mb-4">Contact</div>
          <a
            href={PHONE_TEL}
            data-testid="footer-call-btn"
            className="te-btn te-btn-primary w-full mb-3"
          >
            <Phone size={16} />
            {PHONE}
          </a>
          <p className="text-sm text-zinc-400 mb-2">{HOURS}</p>
          <Link to="/book" data-testid="footer-book-link" className="text-sm text-zinc-300 hover:text-white">
            Send an enquiry →
          </Link>

          <div className="te-overline mt-6 mb-3">Follow & Reviews</div>
          <div className="flex items-center gap-2">
            <a
              href="https://www.facebook.com/profile.php?id=61570216282145"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="Visit Tech Emergency on Facebook"
              className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[var(--te-primary)] hover:text-white text-zinc-400 transition-colors"
            >
              <Facebook size={16} />
            </a>
            <a
              href="https://www.instagram.com/techemergency"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="Visit Tech Emergency on Instagram"
              className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-[var(--te-primary)] hover:text-white text-zinc-400 transition-colors"
            >
              <Instagram size={16} />
            </a>
            <a
              href="https://www.google.com/search?q=tech+emergency+adelaide+reviews"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-google-reviews"
              aria-label="Read Tech Emergency Google reviews"
              className="h-9 px-3 flex items-center gap-1.5 border border-white/10 hover:border-[var(--te-primary)] hover:text-white text-zinc-400 text-xs font-medium transition-colors"
            >
              <Star size={13} fill="currentColor" stroke="none" />
              5.0 · Reviews
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="flex flex-col md:flex-row items-center gap-1 md:gap-3 text-center md:text-left">
            <span>© {new Date().getFullYear()} Tech Emergency. Adelaide, South Australia.</span>
            <span className="hidden md:inline text-zinc-700">·</span>
            <span data-testid="footer-abn">ABN 96 584 230 274</span>
          </div>
          <div className="flex gap-6">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <Link to="/review" className="hover:text-white">Review us</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/admin/login" data-testid="admin-link" className="hover:text-white">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function FloatingCall() {
  // Replaced by the always-visible Call button in the mobile header.
  // Kept exported for backwards compatibility with imports elsewhere.
  return null;
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--te-bg)" }}>
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <StickyCallBar />
    </div>
  );
}
